import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,

  Skeleton,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import PaymentsIcon from "@mui/icons-material/Payments";

import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";

export default function AgentSettings() {
  const { user } = useAuth();

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔔 copy toast
  const [toast, setToast] = useState({
    open: false,
    message: "",
  });

  // 🔐 bank info (current + original)
  const [bank, setBank] = useState({
    account_name: "",
    bank_name: "",
    account_number: "",
  });

  const [originalBank, setOriginalBank] = useState({
    account_name: "",
    bank_name: "",
    account_number: "",
  });

  const [savingBank, setSavingBank] = useState(false);
  const [bankMsg, setBankMsg] = useState("");

  useEffect(() => {
    const loadAgent = async () => {
      try {
        setLoading(true);
        const res = await api.get("/agent/me");

        setAgent(res.data);

        const bankData = {
          account_name: res.data?.account_name || "",
          bank_name: res.data?.bank_name || "",
          account_number: res.data?.account_number || "",
        };

        setBank(bankData);
        setOriginalBank(bankData);
      } catch (err) {
        console.error("Failed to load agent settings", err);
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, []);

  // 🔗 build referral link safely (FIX)
  const referralLink = agent?.referral_code
    ? `${window.location.origin}/register?ref=${agent.referral_code}`
    : "";

  const copy = (text, label = "Copied") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setToast({ open: true, message: `${label} copied successfully` });
  };

  const closeToast = () => {
    setToast({ open: false, message: "" });
  };

  // 🔎 detect changes
  const bankChanged = useMemo(() => {
    return (
      bank.account_name !== originalBank.account_name ||
      bank.bank_name !== originalBank.bank_name ||
      bank.account_number !== originalBank.account_number
    );
  }, [bank, originalBank]);

  const saveBankInfo = async () => {
    setBankMsg("");

    try {
      setSavingBank(true);

      await api.put("/agent/bank-info", bank);

      setBankMsg(
        "✅ Bank details updated. Account verification is required again."
      );

      // reload agent profile to reflect payout_verified = false
      const res = await api.get("/agent/me");
      setAgent(res.data);

      const updatedBank = {
        account_name: res.data?.account_name || "",
        bank_name: res.data?.bank_name || "",
        account_number: res.data?.account_number || "",
      };

      setBank(updatedBank);
      setOriginalBank(updatedBank);
    } catch (err) {
      setBankMsg(
        err?.response?.data?.message ||
          "Failed to update bank information."
      );
    } finally {
      setSavingBank(false);
    }
  };

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3].map((i) => (
          <Grid key={i} item xs={12} md={4}>
            <Skeleton height={160} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!agent) {
    return <Typography color="error">Failed to load agent profile.</Typography>;
  }

  const payoutVerified = agent?.payout_verified === true;

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Agent Settings
      </Typography>

      {/* ================= PROFILE ================= */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <AccountCircleIcon fontSize="large" color="primary" />
          <Typography fontWeight={700}>Profile Information</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Full Name"
              value={user?.full_name || ""}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              value={user?.email || ""}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Agent Since"
              value={new Date(agent.created_at).toLocaleDateString()}
              fullWidth
              disabled
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ================= REFERRAL ================= */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <LinkIcon fontSize="large" color="primary" />
          <Typography fontWeight={700}>Referral Information</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Agent Code"
              value={agent.referral_code}
              fullWidth
              disabled
            />
            <Button
              sx={{ mt: 1 }}
              startIcon={<ContentCopyIcon />}
              onClick={() => copy(agent.referral_code, "Agent code")}
            >
              Copy Code
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Referral Link"
              value={referralLink}
              fullWidth
              disabled
            />
            <Button
              sx={{ mt: 1 }}
              startIcon={<ContentCopyIcon />}
              onClick={() => copy(referralLink, "Referral link")}
              disabled={!referralLink}
            >
              Copy Link
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* ================= BANK / PAYOUT ================= */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <PaymentsIcon fontSize="large" color="primary" />
          <Typography fontWeight={700}>Payout Information</Typography>
        </Box>

        {!payoutVerified && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your bank account is not verified. Payouts are disabled until admin
            verification.
          </Alert>
        )}

        {bankChanged && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Changing bank details will require admin verification again.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Account Holder Name"
              value={bank.account_name}
              onChange={(e) =>
                setBank({ ...bank, account_name: e.target.value })
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Bank Name"
              value={bank.bank_name}
              onChange={(e) =>
                setBank({ ...bank, bank_name: e.target.value })
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Account Number"
              value={bank.account_number}
              onChange={(e) =>
                setBank({ ...bank, account_number: e.target.value })
              }
              fullWidth
              inputProps={{ maxLength: 10 }}
              helperText="10-digit account number"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Verification Status"
              value={payoutVerified ? "Verified" : "Pending Verification"}
              fullWidth
              disabled
            />
          </Grid>
        </Grid>

        {bankMsg && (
          <Typography sx={{ mt: 2 }} color="primary">
            {bankMsg}
          </Typography>
        )}

        <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            onClick={saveBankInfo}
            disabled={!bankChanged || savingBank}
          >
            {savingBank ? "Saving..." : "Save Bank Info"}
          </Button>

          <Button
            variant="outlined"
            disabled={!bankChanged || savingBank}
            onClick={() => {
              setBank(originalBank);
              setBankMsg("");
            }}
          >
            Reset
          </Button>
        </Box>
      </Paper>

      {/* ================= COPY SUCCESS TOAST ================= */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeToast} severity="success" variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
